import { z } from 'zod';

const objectId = z.string().regex(/^[a-f\d]{24}$/i, 'Invalid id');

export const DPIA_ASSESSMENT_TYPES = ['dpia', 'lia', 'tia'];
export const DPIA_STATUSES = ['draft', 'in_review', 'approved', 'rejected', 'archived'];
export const DPIA_OUTCOMES = [
    'approved', 'approved_with_measures', 'prior_consultation_required', 'rejected',
];
export const DPIA_SCREENING_KEYS = [
    'profiling', 'automatedDecision', 'systematicMonitoring', 'sensitiveData',
    'largeScale', 'dataMatching', 'vulnerableSubjects', 'newTechnology', 'blocksRightOrService',
];

export const DpiaScreeningItemSchema = z.object({
    key: z.enum(DPIA_SCREENING_KEYS),
    answer: z.boolean().default(false),
    note: z.string().optional(),
});

export const DpiaRiskItemSchema = z.object({
    _id: z.string().optional(),
    description: z.string().min(1),
    likelihood: z.number().int().min(1).max(5).default(3),
    impact: z.number().int().min(1).max(5).default(3),
    score: z.number().optional(),
    mitigations: z.string().optional(),
    securityMeasures: z.array(objectId).optional(),
    residualRiskLevel: z.enum(['low', 'medium', 'high']).optional(),
});

export const DpiaAssessmentSchema = z.object({
    id: z.string().optional(),
    code: z.string().optional(),
    title: z.string().min(1),
    description: z.string().optional(),
    assessmentType: z.enum(DPIA_ASSESSMENT_TYPES).optional(),
    language: z.enum(['tr', 'en']).optional(),
    templateId: objectId.nullable().optional(),
    inventoryItemId: objectId.nullable().optional(),
    /** @deprecated Use inventoryItemId */
    processingActivityId: objectId.nullable().optional(),
    internationalTransferId: objectId.nullable().optional(),
    screening: z.array(DpiaScreeningItemSchema).optional(),
    necessityAssessment: z.string().optional(),
    proportionalityAssessment: z.string().optional(),
    risks: z.array(DpiaRiskItemSchema).optional(),
    lia: z.object({
        purposeTest: z.string().optional(),
        necessityTest: z.string().optional(),
        balancingTest: z.string().optional(),
        conclusion: z.enum(['passes', 'fails', 'inconclusive']).nullable().optional(),
    }).optional(),
    tia: z.object({
        destinationCountry: z.string().optional(),
        destinationLawAssessment: z.string().optional(),
        transferMechanism: z.string().optional(),
        supplementaryMeasures: z.string().optional(),
        conclusion: z.enum(['adequate', 'adequate_with_measures', 'inadequate']).nullable().optional(),
    }).optional(),
    dpoOpinion: z.string().optional(),
    outcome: z.enum(DPIA_OUTCOMES).nullable().optional(),
    approvalDecision: z.enum(['accept', 'reject', 'conditional']).nullable().optional(),
    residualRiskLevel: z.enum(['low', 'medium', 'high']).nullable().optional(),
    completedBy: objectId.nullable().optional(),
    linkedRiskIds: z.array(objectId).optional(),
    status: z.enum(DPIA_STATUSES).optional(),
    customFields: z.record(z.any()).optional(),
    createdAt: z.date().optional(),
    updatedAt: z.date().optional(),
});

export const CreateDpiaAssessmentSchema = DpiaAssessmentSchema.omit({
    id: true, createdAt: true, updatedAt: true, status: true, approvalDecision: true, completedBy: true,
});
export const UpdateDpiaAssessmentSchema = DpiaAssessmentSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial();
